import React, { useState } from "react";
import {
  ActionIcon,
  Button,
  Code,
  CopyButton,
  Group,
  Paper,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import {
  IconCheck,
  IconCopy,
  IconX,
  IconEye,
  IconEyeOff,
} from "@tabler/icons-react";

function shorten(addr: string, head = 10, tail = 10) {
  if (!addr) return "";
  if (addr.length <= head + tail + 3) return addr;
  return `${addr.slice(0, head)}…${addr.slice(-tail)}`;
}

export function DestinationAddress({
  address,
  label = "Destination address",
  onClear,
}: {
  address: string;
  label?: string;
  onClear?: () => void;
}) {
  const [showFullAddress, setShowFullAddress] = useState(false);

  return (
    <Paper withBorder radius="md" p="md">
      <Stack gap={6}>
        <Group justify="space-between" align="center">
          <Text size="sm" c="dimmed" fw={600}>
            {label}
          </Text>

          <CopyButton value={address} timeout={1200}>
            {({ copied, copy }) => (
              <Tooltip label={copied ? "Copied" : "Copy"} withArrow>
                <ActionIcon
                  variant="subtle"
                  onClick={copy}
                  aria-label="Copy destination address"
                >
                  {copied ? <IconCheck size={18} /> : <IconCopy size={18} />}
                </ActionIcon>
              </Tooltip>
            )}
          </CopyButton>
        </Group>

        {/* compact preview or full address */}
        <Code
          block
          style={{
            userSelect: "text",
            whiteSpace: showFullAddress ? "pre-wrap" : undefined,
            wordBreak: showFullAddress ? "break-all" : undefined,
          }}
        >
          {showFullAddress ? address : shorten(address, 14, 12)}
        </Code>

        {/* Action buttons */}
        <Group gap="xs">
          <Button
            variant="subtle"
            size="compact-xs"
            leftSection={
              showFullAddress ? <IconEyeOff size={14} /> : <IconEye size={14} />
            }
            onClick={() => setShowFullAddress(!showFullAddress)}
          >
            {showFullAddress ? "Hide full address" : "Show full address"}
          </Button>

          {onClear && (
            <Button
              variant="subtle"
              color="red"
              size="compact-xs"
              leftSection={<IconX size={14} />}
              onClick={onClear}
            >
              Clear
            </Button>
          )}
        </Group>
      </Stack>
    </Paper>
  );
}
